import React, { Component } from 'react';
import { useRouteError } from 'react-router-dom';
import { Card, Button, Collapse, Typography, Space, Result } from 'antd';
import { 
  CloseCircleOutlined, 
  ReloadOutlined, 
  HomeOutlined, 
  BugOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { 
  isChunkLoadError, 
  isMimeTypeError, 
  getChunkErrorMessage,
  clearReloadAttempts 
} from '../../utils/chunkLoadHandler';

const { Panel } = Collapse;
const { Text, Title, Paragraph } = Typography;

/**
 * ErrorFallback Component - Displays when an error is caught
 * Matches the dashboard theme with consistent styling
 */
export const ErrorFallback = ({ error, resetError, componentStack, sentryEventId }) => {
  // Check if this is a chunk load error
  const isChunkError = isChunkLoadError(error);
  const isMimeError = isMimeTypeError(error);

  // Hide stack traces in production
  const showStackTraces = process.env.NODE_ENV !== 'production';

  const handleGoHome = () => {
    // Clear reload attempts when navigating away
    clearReloadAttempts();
    window.location.href = '/dashboard';
  };

  const handleReload = () => {
    // Clear reload attempts on manual reload
    clearReloadAttempts();
    
    if (resetError) {
      resetError();
    } else {
      // Add cache-busting parameter for chunk errors
      if (isChunkError) {
        const url = new URL(window.location.href);
        url.searchParams.set('_r', Date.now().toString());
        window.location.href = url.toString();
      } else {
        window.location.reload();
      }
    }
  };

  // Get appropriate title and message based on error type
  const getTitle = () => {
    if (isChunkError) {
      return 'Application Update Required';
    }
    return 'Something Went Wrong';
  };

  const getSubtitle = () => {
    if (isChunkError) {
      return 'The application has been updated. Please refresh to load the latest version.';
    }
    return "We've encountered an unexpected error. Our team has been notified.";
  };

  const getErrorMessage = () => {
    if (isChunkError) {
      return getChunkErrorMessage(error);
    }
    return error?.message || 'An unknown error occurred';
  };

  return (
    <div 
      style={{ 
        minHeight: '100vh',
        background: '#f5f7fb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
      }}
    >
      <Card
        className="error-boundary-card"
        style={{
          maxWidth: '800px',
          width: '100%',
          background: 'white',
          borderRadius: '12px',
          border: 'none',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden'
        }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Header Section with Gradient Background */}
        <div 
          style={{
            background: isChunkError 
              ? 'linear-gradient(135deg, #1890ff 0%, #36cfc9 100%)' // Blue gradient for chunk errors
              : 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)', // Red gradient for other errors
            padding: '32px 24px',
            textAlign: 'center',
            color: 'white'
          }}
        >
          <div 
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              backdropFilter: 'blur(10px)'
            }}
          >
            {isChunkError ? (
              <SyncOutlined style={{ fontSize: '32px', color: 'white' }} />
            ) : (
              <CloseCircleOutlined style={{ fontSize: '32px', color: 'white' }} />
            )}
          </div>
          
          <Title 
            level={3} 
            style={{ 
              color: 'white', 
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: 600
            }}
          >
            {getTitle()}
          </Title>
          
          <Paragraph style={{ 
            color: 'rgba(255, 255, 255, 0.9)', 
            margin: 0,
            fontSize: '14px'
          }}>
            {getSubtitle()}
          </Paragraph>
        </div>

        {/* Content Section */}
        <div style={{ padding: '24px' }}>
          {/* Error Message Card */}
          <div 
            style={{
              background: isChunkError ? '#e6f7ff' : '#fff2f0',
              border: isChunkError ? '1px solid #91d5ff' : '1px solid #ffccc7',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <ExclamationCircleOutlined 
                style={{ 
                  fontSize: '20px', 
                  color: isChunkError ? '#1890ff' : '#ff4d4f',
                  marginTop: '2px',
                  flexShrink: 0
                }} 
              />
              <div style={{ flex: 1 }}>
                <Text strong style={{ color: '#33394c', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                  {isChunkError ? 'Update Required' : 'Error Message'}
                </Text>
                <Text style={{ color: '#6f7687', fontSize: '13px', display: 'block' }}>
                  {getErrorMessage()}
                </Text>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <Space size="middle" style={{ width: '100%', justifyContent: 'center', marginBottom: '24px' }}>
            <Button
              type="primary"
              icon={isChunkError ? <SyncOutlined /> : <ReloadOutlined />}
              onClick={handleReload}
              size="large"
              style={{
                background: '#1890ff',
                borderColor: '#1890ff',
                borderRadius: '8px',
                height: '40px',
                padding: '0 24px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
              }}
            >
              {isChunkError ? 'Refresh Now' : 'Try Again'}
            </Button>
            
            <Button
              icon={<HomeOutlined />}
              onClick={handleGoHome}
              size="large"
              style={{
                borderRadius: '8px',
                height: '40px',
                padding: '0 24px',
                fontWeight: 500,
                borderColor: '#d9d9d9',
                color: '#33394c'
              }}
            >
              Go to Dashboard
            </Button>
          </Space>

          {/* Collapsible Error Details - only show in non-production */}
          {showStackTraces && (
            <Collapse
              ghost
              style={{
                background: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #f0f0f0'
              }}
            >
              <Panel
                header={
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#6f7687',
                    fontSize: '13px'
                  }}>
                    <BugOutlined />
                    Technical Details (for debugging)
                  </span>
                }
                key="1"
                style={{ padding: '0 16px' }}
              >
                <div style={{ padding: '16px 0' }}>
                  {/* Error Stack */}
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{
                      color: '#33394c',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      Error Stack
                    </Text>
                    <pre
                      style={{
                        background: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#33394c',
                        overflow: 'auto',
                        maxHeight: '200px',
                        margin: 0,
                        fontFamily: "'Courier New', monospace",
                        border: '1px solid #e8e8e8'
                      }}
                    >
                      {error?.stack || 'No stack trace available'}
                    </pre>
                  </div>

                  {/* Component Stack */}
                  {componentStack && (
                    <div>
                      <Text strong style={{
                        color: '#33394c',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'block',
                        marginBottom: '8px'
                      }}>
                        Component Stack
                      </Text>
                      <pre
                        style={{
                          background: '#f5f5f5',
                          padding: '12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#33394c',
                          overflow: 'auto',
                          maxHeight: '200px',
                          margin: 0,
                          fontFamily: "'Courier New', monospace",
                          border: '1px solid #e8e8e8'
                        }}
                      >
                        {componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </Panel>
            </Collapse>
          )}

          {/* Helpful Tips */}
          <div 
            style={{
              marginTop: '24px',
              padding: '16px',
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '8px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <InfoCircleOutlined 
                style={{ 
                  fontSize: '18px', 
                  color: '#1890ff',
                  marginTop: '2px',
                  flexShrink: 0
                }} 
              />
              <div>
                <Text strong style={{ color: '#33394c', fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                  What you can try:
                </Text>
                <ul style={{ 
                  margin: '4px 0 0 0', 
                  paddingLeft: '16px', 
                  color: '#6f7687',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  <li>Refresh the page and try again</li>
                  <li>Clear your browser cache and cookies</li>
                  <li>Check your internet connection</li>
                  <li>If the problem persists, contact support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div 
          style={{
            padding: '16px 24px',
            background: '#fafafa',
            borderTop: '1px solid #f0f0f0',
            textAlign: 'center'
          }}
        >
          <Text style={{ color: '#8c8c8c', fontSize: '12px' }}>
            {sentryEventId ? `Error ID: ${sentryEventId}` : 'Error ID not available'} •
            If this issue persists, please report it to the development team
          </Text>
        </div>
      </Card>
    </div>
  );
};

/**
 * RouteError Component - For use with React Router errorElement
 * Uses useRouteError to get the actual error from React Router
 */
export const RouteError = () => {
  const routeError = useRouteError();
  
  // Convert the route error to a proper Error object with stack trace
  const error = React.useMemo(() => {
    if (routeError instanceof Error) {
      // If it's already an Error object, use it directly
      return routeError;
    } else if (typeof routeError === 'string') {
      // If it's a string, create an Error from it
      return new Error(routeError);
    } else if (routeError && typeof routeError === 'object') {
      // If it's an object with a message property
      if (routeError.message) {
        const err = new Error(routeError.message);
        // Preserve the original stack if available
        if (routeError.stack) {
          err.stack = routeError.stack;
        }
        return err;
      }
      // If it's some other object, stringify it
      return new Error(JSON.stringify(routeError));
    }
    // Fallback error message
    return new Error("This page failed to load properly.");
  }, [routeError]);

  return <ErrorFallback error={error} />;
};

/**
 * ErrorBoundary Class Component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      sentryEventId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Send to Sentry using the imported @sentry/react module
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack
      }
    }).then(eventId => {
      // Store the Sentry event ID for display in error UI
      this.setState({ sentryEventId: eventId });
    }).catch(() => {
      // Sentry capture failed, use fallback ID
      this.setState({ sentryEventId: null });
    });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      sentryEventId: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.resetError}
          componentStack={this.state.errorInfo?.componentStack}
          sentryEventId={this.state.sentryEventId}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * withErrorBoundary HOC - Wraps components with error boundary
 */
export const withErrorBoundary = (WrappedComponent, fallback = null) => {
  return class extends Component {
    render() {
      return (
        <ErrorBoundary fallback={fallback}>
          <WrappedComponent {...this.props} />
        </ErrorBoundary>
      );
    }
  };
};

export default ErrorBoundary;
